import {
  Grid,
  makeStyles,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { Form, Field } from 'formik';
import cx from 'classnames';
import { FormattedPhone } from 'components/Shared/FormattedPhone';
import FormikTextField from 'components/FormikFields/FormikTextField';
import {
  PATIENT_LANGUAGE,
  PATIENT_LANGUAGE_SELECTOR_OPTIONS,
  PATIENT_RACE_SELECTOR_OPTIONS,
} from 'components/Patients/constants';
import HelperText from 'components/Shared/HelperText';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { getFormattedDate } from '../../../../helpers/localize';
import CalendarPopover from '../../../FormikFields/Calendar/CalendarPopover';
import Select from '../../../FormikFields/ComplexSelect/Select';
import AddressForm from '../../../Accounts/Item/AddressForm';
import { useSelector } from 'react-redux';
import ToggleButtonGroup from './ToggleButtonGroup';

const useStyles = makeStyles(theme => ({
  datepickerWrapper: {
    padding: '5px 12px',
    borderRadius: 8,
    border: `1px solid rgba(0, 0, 0, 0.23)`,
    minWidth: 274,
    minHeight: 40,
    height: 40,
  },
  title: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  checkbox: {
    marginLeft: -theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
  },
  checkboxWithoutMarginBottom: {
    marginLeft: -theme.spacing(1.5),
  },
  divider: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dividerSpacing: {
    marginTop: 0,
  },
  radio: {
    color: theme.colors.grey3,
  },
  checkedRadio: {
    color: theme.colors.lightBlue,
  },
  roleLabel: {
    margin: theme.spacing(0, 0, 1.875, 0),
    width: 125,
    color: theme.colors.blue2,
  },
  roleLabelCheckbox: {
    margin: theme.spacing(1.875, 0, 1.175, 0),
    color: theme.colors.blue2,
  },
  radioContainer: {
    width: 210,
  },
  mb1: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  m2: {
    marginRight: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),

    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2.5),
    },
  },
  section: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors.blue2,
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.colors.lightBlue}`,
  },
  fieldWithIcon: {
    position: 'relative',
  },
  infoIcon: {
    position: 'absolute',
    right: 4,
    top: '42%',
    transform: 'translateY(-50%)',
    minWidth: 26,
    minHeight: 40,
    padding: 4,
    '& svg': {
      fontSize: 20,
      color: theme.palette.action.disabled,
    },
    '&:hover': {
      backgroundColor: 'transparent',
      '& svg': {
        color: theme.colors.lightBlue,
      },
    },
  },
  tooltipPopper: {
    '& .MuiTooltip-tooltip': {
      backgroundColor: theme.colors.blue2,
      color: '#ffffff',
      fontSize: 13,
      padding: theme.spacing(1.5, 2),
      maxWidth: 300,
      lineHeight: 1.5,
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    '& .MuiTooltip-arrow': {
      color: theme.colors.blue2,
    },
  },
  infoHint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    backgroundColor: `rgba(0, 168, 255, 0.07)`,
    '& svg': {
      fontSize: 16,
      color: theme.colors.lightBlue,
      marginTop: 1,
      flexShrink: 0,
    },
    '& span': {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: theme.colors.gray4 || '#818FA0',
    },
  },
  modernLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'block',
  },
}));

const PatientForm = ({ values, backendErrors, actions, hideBHI = false }) => {
  const classes = useStyles();
  const {
    en: { forms },
  } = useSelector(state => state.i18n);

  return (
    <Form className={classes.form}>
      {/* Basic Information Section */}
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Personal Information</div>
        <Grid container justify="space-between" spacing={4} className={classes.mb1}>
          <Grid item xs={12} sm={4}>
            <FormikTextField
              type="text"
              name="mrnNumber"
              label={forms.mrnNumber}
              backendError={backendErrors.mrnNumber}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormikTextField
              type="text"
              name="firstName"
              label={forms.firstName}
              backendError={backendErrors.firstName}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormikTextField
              type="text"
              name="lastName"
              label={forms.lastName}
              backendError={backendErrors.lastName}
              required
            />
          </Grid>
        </Grid>
        <Grid container justify="space-between" spacing={4}>
          <Grid item sm={12} md={4}>
            <FormikTextField
              type="tel"
              name="phone.phone"
              label={forms.phone}
              backendError={backendErrors?.phone?.phone}
              InputProps={{
                inputComponent: FormattedPhone,
              }}
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <div className={classes.fieldWithIcon}>
              <FormikTextField
                type="tel"
                name="preferredPhone.phone"
                label={forms.preferredPhone}
                backendError={backendErrors?.preferredPhone?.phone}
                InputProps={{
                  inputComponent: FormattedPhone,
                }}
                disabled
              />
              <Tooltip
                title={forms.preferredPhoneDescription}
                placement="top"
                arrow
                classes={{ popper: classes.tooltipPopper }}
              >
                <IconButton
                  className={classes.infoIcon}
                  size="small"
                  disableRipple
                  disableFocusRipple
                >
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </IconButton>
              </Tooltip>
            </div>
          </Grid>
          <Grid item sm={12} md={4}>
            <FormikTextField
              type="text"
              name="email"
              label={forms.email}
              backendError={backendErrors.email}
            />
          </Grid>
        </Grid>
      </div>

      {/* Demographics & Medical Information Section */}
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Demographics & Medical Information</div>
        <Grid container justify="space-between" spacing={4}>
          <Grid item xs={12} md={4}>
            <Field name="dateOfBirth">
              {({ field, form }) => {
                let date = null;
                if (field.value) {
                  // add offset in minutes to work with date like in UTC
                  const newDate = new Date(field.value);
                  if (field.value.includes('T00:00Z')) {
                    const userTimezoneOffset = newDate.getTimezoneOffset() * 60000;
                    date = new Date(newDate.getTime() + userTimezoneOffset);
                  } else {
                    date = newDate;
                  }
                }
                return (
                  <FormControl
                    {...{
                      required: true,
                      error: !!backendErrors.dateOfBirth,
                    }}
                  >
                    <FormLabel
                      {...{
                        className: classes.roleLabel,
                      }}
                    >
                      {forms.dateOfBirth}
                    </FormLabel>
                    <div className={classes.datepickerWrapper}>
                      <CalendarPopover
                        onChange={newDate => {
                          if (!newDate) {
                            // clear filter
                            form.setFieldValue(field.name, '');
                          } else {
                            // valid date
                            form.setFieldValue(field.name, getFormattedDate(newDate));
                          }
                        }}
                        date={date}
                        maxDate={new Date()}
                      />
                    </div>
                    <HelperText>{backendErrors.dateOfBirth}</HelperText>
                  </FormControl>
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} md={4}>
            <Field name="genderAtBirth">
              {({ field, form }) => (
                <div>
                  <label className={classes.modernLabel}>{forms.genderAtBirth} *</label>
                  <ToggleButtonGroup
                    value={field.value}
                    onChange={value => form.setFieldValue(field.name, value)}
                    name="genderAtBirth"
                    options={[
                      { value: 'female', label: 'Female' },
                      { value: 'male', label: 'Male' },
                    ]}
                  />
                  {backendErrors.genderAtBirth && (
                    <HelperText error>{backendErrors.genderAtBirth}</HelperText>
                  )}
                </div>
              )}
            </Field>
          </Grid>
          <Grid item xs={12} md={4}>
            <Field name="hasDrugAllergies">
              {({ field, form }) => {
                let { value } = field;
                if (field.value === undefined) {
                  value = null;
                }
                return (
                  <div>
                    <label className={classes.modernLabel}>{forms.drugAllergies} *</label>
                    <ToggleButtonGroup
                      value={value}
                      onChange={val => form.setFieldValue(field.name, val)}
                      name="hasDrugAllergies"
                      options={[
                        { value: false, label: forms.no },
                        { value: true, label: forms.yes },
                        { value: null, label: forms.unknown },
                      ]}
                    />
                  </div>
                );
              }}
            </Field>
          </Grid>
        </Grid>
        {values.hasDrugAllergies && (
          <Grid container spacing={4} style={{ marginTop: 16 }}>
            <Grid item xs={12}>
              <FormikTextField
                type="text"
                name="drugAllergies"
                label={forms.drugList}
                multiline
                rows={4}
                maxRows={4}
                backendError={backendErrors.drugAllergies}
              />
            </Grid>
          </Grid>
        )}
      </div>

      {/* Language Preference & Race Section */}
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Language Preference & Race</div>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Field name="patientLanguage" type="checkbox">
              {({ field, form }) => {
                return (
                  <Select
                    dataKey="patientLanguage"
                    filterValue={field.value ?? PATIENT_LANGUAGE.UNKNOWN}
                    columnData={{
                      options: PATIENT_LANGUAGE_SELECTOR_OPTIONS,
                    }}
                    onChange={(name, value) => {
                      form.setFieldValue(name, value);
                    }}
                    label={`${forms.patientLanguage}`}
                    labelWidth={78}
                    error={backendErrors.patientLanguage}
                    className={cx(classes.margin0)}
                  />
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field name="race" type="checkbox">
              {({ field, form }) => {
                return (
                  <Select
                    dataKey="race"
                    filterValue={field.value ?? ''}
                    columnData={{
                      options: PATIENT_RACE_SELECTOR_OPTIONS,
                    }}
                    onChange={(name, value) => {
                      form.setFieldValue(name, value);
                    }}
                    label={`${forms.race || 'Race'}`}
                    labelWidth={78}
                    error={backendErrors.race}
                    className={cx(classes.margin0)}
                  />
                );
              }}
            </Field>
          </Grid>
        </Grid>
      </div>

      {/* Address Information Section */}
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Address Information</div>
        <AddressForm fullWidth prefix="address" backendErrors={backendErrors} hasMobileLayout />
      </div>

      {/* Communication Preferences Section */}
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Communication Preferences</div>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Field name="smsOptIn" type="checkbox">
              {({ field, form }) => {
                return (
                  <Grid container direction="column">
                    <FormLabel className={classes.roleLabelCheckbox}>{forms.smsOptIn}</FormLabel>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={field.name}
                          checked={field.value}
                          color="primary"
                          onChange={(e, checked) => {
                            form.setFieldValue(field.name, checked);
                          }}
                          disabled={false}
                        />
                      }
                      className={classes.checkbox}
                      label={forms.smsOptInDescription}
                    />
                  </Grid>
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field name="hasEmailSubscription" type="checkbox">
              {({ field, form }) => {
                return (
                  <Grid container direction="column">
                    <FormLabel className={classes.roleLabelCheckbox}>
                      {forms.hasEmailSubscription}
                    </FormLabel>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name={field.name}
                          checked={field.value}
                          color="primary"
                          onChange={(e, checked) => {
                            form.setFieldValue(field.name, checked);
                          }}
                          disabled={false}
                        />
                      }
                      className={classes.checkbox}
                      label={forms.hasEmailSubscriptionDescription}
                    />
                  </Grid>
                );
              }}
            </Field>
          </Grid>
          {!hideBHI && (
            <Grid item xs={12} sm={12}>
              <Field name="bhiStatus" type="checkbox">
                {({ field }) => {
                  return (
                    <Grid container direction="column">
                      <FormLabel className={classes.roleLabelCheckbox}>{forms.bhiStatus}</FormLabel>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name={field.name}
                            checked={!!field.value}
                            color="primary"
                            disabled
                          />
                        }
                        className={classes.checkboxWithoutMarginBottom}
                        label={forms.bhiStatusDescription}
                      />
                      <div className={classes.infoHint}>
                        <InfoOutlinedIcon />
                        <span>{forms.bhiStatusHint}</span>
                      </div>
                    </Grid>
                  );
                }}
              </Field>
            </Grid>
          )}
        </Grid>
      </div>

      {actions}
    </Form>
  );
};

export default PatientForm;
